import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MapPin, ArrowLeft, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Géocodage des codes postaux récupéré dynamiquement depuis l'API gouvernementale française

const MapGeocenter = ({ center, bounds, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    } else if (center) {
      map.setView(center, zoom || 10);
    }
  }, [center, bounds, zoom, map]);
  return null;
};

const CartographieInteractive = () => {
  const navigate = useNavigate();
  const [communesData, setCommunesData] = useState([]);
  const [epciData, setEpciData] = useState([]);
  const [epciList, setEpciList] = useState([]);
  const [geocodedCoords, setGeocodedCoords] = useState({});
  const [communeLabels, setCommuneLabels] = useState({});
  const [filteredData, setFilteredData] = useState(null);
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [selectedEpci, setSelectedEpci] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]); // Centre France

  const fetchInitialData = useCallback(async () => {
    try {
      const [communesRes, epciRes, epciListRes, geocodeRes] = await Promise.all([
        axios.get(`${API}/survey/by-commune`, { withCredentials: true }),
        axios.get(`${API}/survey/by-epci`, { withCredentials: true }),
        axios.get(`${API}/epci/list`, { withCredentials: true }),
        axios.get(`${API}/geocode`, { withCredentials: true })
      ]);
      setCommunesData(communesRes.data);
      setEpciData(epciRes.data);
      setEpciList(epciListRes.data.epci_list);
      setGeocodedCoords(geocodeRes.data.geocoded || {});
      setCommuneLabels(geocodeRes.data.labels || {});
    } catch (error) {
      // Silent fail - data will remain empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleCommuneSelect = async (postalCode) => {
    setSelectedCommune(postalCode);
    setSelectedEpci(null); // Reset EPCI filter
    
    if (!postalCode || postalCode === 'all') {
      setFilteredData(null);
      setMapCenter([46.603354, 1.888334]);
      return;
    }

    try {
      const response = await axios.get(`${API}/survey/filter?postal_code=${postalCode}`, { withCredentials: true });
      setFilteredData(response.data);
      
      // Use real geocoded coordinates
      const cleanedCode = postalCode.toString().replace('.0', '');
      const coords = geocodedCoords[cleanedCode];
      if (coords) {
        setMapCenter(coords);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handleEpciSelect = async (epci) => {
    setSelectedEpci(epci);
    setSelectedCommune(null); // Reset commune filter
    
    if (!epci || epci === 'all') {
      setFilteredData(null);
      setMapCenter([46.603354, 1.888334]);
      return;
    }

    try {
      // Use proper URL encoding
      const encodedEpci = encodeURIComponent(epci);
      const response = await axios.get(`${API}/survey/filter-by-epci?epci=${encodedEpci}`, { withCredentials: true });
      setFilteredData(response.data);
      
      // Center on real coords of first commune in this EPCI
      if (response.data.responses && response.data.responses.length > 0) {
        for (const r of response.data.responses) {
          if (r.rs6) {
            const cleanedCode = r.rs6.toString().replace('.0', '');
            const coords = geocodedCoords[cleanedCode];
            if (coords) {
              setMapCenter(coords);
              break;
            }
          }
        }
      }
    } catch (error) {
      // Silent fail
    }
  };

  const getMarkerColor = (percentageEquipped) => {
    if (percentageEquipped >= 50) return '#16A34A'; // Green
    if (percentageEquipped >= 30) return '#D4AF37'; // Gold
    return '#DC2626'; // Red
  };

  const getMarkerSize = (total) => {
    if (total >= 20) return 15;
    if (total >= 10) return 12;
    if (total >= 5) return 10;
    return 8;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F203C] mx-auto mb-4"></div>
          <p className="text-[#475569]">Chargement de la carte...</p>
        </div>
      </div>
    );
  }

  const displayData = filteredData?.stats || {
    total_responses: communesData.reduce((sum, c) => sum + c.total, 0),
    with_batardeaux: communesData.reduce((sum, c) => sum + c.with_batardeaux, 0),
    without_batardeaux: communesData.reduce((sum, c) => sum + c.without_batardeaux, 0),
    percentage_equipped: communesData.length > 0 
      ? Math.round((communesData.reduce((sum, c) => sum + c.with_batardeaux, 0) / communesData.reduce((sum, c) => sum + c.total, 0)) * 100 * 10) / 10
      : 0
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F203C] text-white py-6 px-6 mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                  <MapPin className="w-8 h-8" />
                  Cartographie Interactive
                </h1>
                <p className="text-sm opacity-90 mt-1">Analyse géographique des réponses</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Filters */}
        <Card className="border-[#E2E8F0] shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-[#0F203C]">Filtres</CardTitle>
            <CardDescription>Filtrez par EPCI porteur de PAPI ou par commune spécifique</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* EPCI Filter */}
              <div>
                <label className="text-sm font-semibold text-[#0F203C] mb-2 block">
                  EPCI Porteur de PAPI
                </label>
                <Select value={selectedEpci || 'all'} onValueChange={handleEpciSelect}>
                  <SelectTrigger data-testid="epci-filter" className="w-full">
                    <SelectValue placeholder="Tous les EPCI" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les EPCI</SelectItem>
                    {epciList.map((epci) => {
                      const epciStats = epciData.find(e => e.epci === epci);
                      return (
                        <SelectItem key={epci} value={epci}>
                          {epci} {epciStats && `(${epciStats.total} réponses)`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Commune Filter */}
              <div>
                <label className="text-sm font-semibold text-[#0F203C] mb-2 block">
                  Commune (Code Postal)
                </label>
                <Select value={selectedCommune || 'all'} onValueChange={handleCommuneSelect}>
                  <SelectTrigger data-testid="commune-filter" className="w-full">
                    <SelectValue placeholder="Toutes les communes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les communes</SelectItem>
                    {communesData.map((commune) => {
                      const cleanedCode = commune.postal_code.toString().replace('.0', '');
                      const label = communeLabels[cleanedCode] || cleanedCode;
                      return (
                        <SelectItem key={commune.postal_code} value={commune.postal_code}>
                          {label} ({commune.total} réponses)
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active filters badges */}
            <div className="flex gap-2 mt-4 flex-wrap">
              {selectedEpci && selectedEpci !== 'all' && (
                <Badge variant="secondary" className="text-sm bg-[#D4AF37]/20 text-[#0F203C] border-[#D4AF37]">
                  EPCI : {selectedEpci}
                </Badge>
              )}
              {selectedCommune && selectedCommune !== 'all' && (
                <Badge variant="secondary" className="text-sm bg-[#0F203C]/10 text-[#0F203C]">
                  Commune : {selectedCommune}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-[#475569] mb-1">Réponses</div>
              <div className="text-3xl font-bold text-[#0F203C]">{displayData.total_responses}</div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-[#475569] mb-1">Équipés</div>
              <div className="text-3xl font-bold text-[#16A34A] flex items-center gap-2">
                {displayData.with_batardeaux}
                <TrendingUp className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-[#475569] mb-1">Non équipés</div>
              <div className="text-3xl font-bold text-[#DC2626] flex items-center gap-2">
                {displayData.without_batardeaux}
                <TrendingDown className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-[#475569] mb-1">Taux d'équipement</div>
              <div className="text-3xl font-bold text-[#D4AF37]">{displayData.percentage_equipped}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Map */}
        <Card className="border-[#E2E8F0] shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-[#0F203C]">Carte de France</CardTitle>
            <CardDescription>
              <div className="flex gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#16A34A]"></div>
                  <span className="text-xs">≥ 50% équipés</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#D4AF37]"></div>
                  <span className="text-xs">30-49% équipés</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#DC2626]"></div>
                  <span className="text-xs">&lt; 30% équipés</span>
                </div>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[600px] rounded-md overflow-hidden border border-[#E2E8F0]">
              {(() => {
                // Calculate which markers to display based on filters
                const visibleCommunes = communesData.filter((commune) => {
                  const cleanedCode = commune.postal_code.toString().replace('.0', '');
                  if (!geocodedCoords[cleanedCode]) return false;
                  
                  if (selectedCommune && selectedCommune !== 'all') {
                    return commune.postal_code === selectedCommune;
                  }
                  
                  if (selectedEpci && selectedEpci !== 'all') {
                    // Check if this commune belongs to the selected EPCI
                    // We need to verify via the responses
                    if (filteredData?.responses) {
                      return filteredData.responses.some(r => 
                        r.rs6 && r.rs6.toString() === commune.postal_code.toString()
                      );
                    }
                  }
                  
                  return true;
                });
                
                // Calculate bounds from visible markers
                const visibleBounds = visibleCommunes
                  .map(c => geocodedCoords[c.postal_code.toString().replace('.0', '')])
                  .filter(Boolean);
                
                return (
                  <MapContainer
                    center={mapCenter}
                    zoom={6}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapGeocenter bounds={visibleBounds} />
                    
                    {visibleCommunes.map((commune) => {
                      const cleanedCode = commune.postal_code.toString().replace('.0', '');
                      const coords = geocodedCoords[cleanedCode];
                      const label = communeLabels[cleanedCode] || cleanedCode;
                      
                      return (
                        <CircleMarker
                          key={commune.postal_code}
                          center={coords}
                          radius={getMarkerSize(commune.total)}
                          fillColor={getMarkerColor(commune.percentage_equipped)}
                          color="#fff"
                          weight={2}
                          opacity={1}
                      fillOpacity={0.8}
                    >
                      <Popup>
                        <div className="p-2">
                          <div className="font-bold text-[#0F203C] mb-2">{label}</div>
                          <div className="text-xs text-[#475569] mb-2">Code postal : {cleanedCode}</div>
                          <div className="text-sm space-y-1">
                            <div>Total : <strong>{commune.total}</strong> réponses</div>
                            <div className="text-[#16A34A]">Équipés : <strong>{commune.with_batardeaux}</strong></div>
                            <div className="text-[#DC2626]">Non équipés : <strong>{commune.without_batardeaux}</strong></div>
                            <div className="text-[#D4AF37] font-semibold mt-2">
                              Taux : {commune.percentage_equipped}%
                            </div>
                            <div className="text-[#0F203C] mt-2">
                              Conscience risque : {commune.percentage_risk_aware}%
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
                );
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="border-[#D4AF37] bg-[#D4AF37]/5 mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-[#0F203C] flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#D4AF37]" />
              Légende et informations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-semibold text-[#0F203C] mb-2">Carte interactive :</p>
                <div className="space-y-1 text-[#475569] ml-4">
                  <p>• <strong>Taille du cercle</strong> : Nombre de réponses dans la commune</p>
                  <p>• <strong>Couleur du cercle</strong> : Taux d'équipement en batardeaux</p>
                  <p>• Cliquez sur un cercle pour voir les détails de la commune</p>
                </div>
              </div>
              
              <div className="pt-2 border-t border-[#D4AF37]/20">
                <p className="font-semibold text-[#0F203C] mb-2">Filtres disponibles :</p>
                <div className="space-y-1 text-[#475569] ml-4">
                  <p>• <strong>Par EPCI</strong> : Établissement Public de Coopération Intercommunale porteur de PAPI</p>
                  <p>• <strong>Par Commune</strong> : Code postal spécifique</p>
                  <p className="italic text-xs mt-2">Les filtres se réinitialisent mutuellement pour une analyse ciblée</p>
                </div>
              </div>

              {epciData.length > 0 && (
                <div className="pt-2 border-t border-[#D4AF37]/20">
                  <p className="font-semibold text-[#0F203C] mb-2">Statistiques par EPCI :</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {epciData.slice(0, 6).map((epci) => (
                      <div key={epci.epci} className="bg-white p-3 rounded border border-[#E2E8F0]">
                        <div className="font-semibold text-[#0F203C] text-xs mb-1">{epci.epci}</div>
                        <div className="flex justify-between text-xs text-[#475569]">
                          <span>{epci.total} réponses</span>
                          <span className="font-semibold" style={{ color: epci.percentage_equipped >= 50 ? '#16A34A' : epci.percentage_equipped >= 30 ? '#D4AF37' : '#DC2626' }}>
                            {epci.percentage_equipped}% équipés
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CartographieInteractive;
